import { transition } from './animations';
import { vars } from '../theme';
import colors from './colors';
import styled, { css, CSSProperties } from 'styled-components';
import { proximaNova as _proximaNova } from './fonts';
import { hexToRgbString } from '../utils/color.utils';

export const visuallyHidden = css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

export const flexCenterAll = css`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const inlineFlexCenterAll = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

export const flexCenterVertical = css`
    display: flex;
    align-items: center;
`;

export const flexColumn = css`
    display: flex;
    flex-direction: column;
`;

/* Custom Scrollbar */
export const scrollThumb = css`
    border-radius: ${vars.radius.xs};
    background: ${colors.neutral700};
`;
export const leftScrollBar = css`
    direction: rtl;
    > * {
        direction: ltr;
    }
`;
export const scrollTrack = css`
    background: ${colors.neutral200};
    border-radius: ${vars.radius.xs};
    padding: 2px;
`;

export const scrollVertical = css`
    display: block !important;
    overflow-y: hidden;
    width: 5px;
`;

export const scrollHorizontal = css`
    display: block !important;
    overflow-y: hidden;
    height: 5px;
    background: ${colors.neutral700};
`;

/* Scroll Shadow */
const OPPOSITE_DIRECTIONS: Record<string, string> = {
    'to top': 'bottom',
    'to bottom': 'top',
    'to left': 'right',
    'to right': 'left'
};

function getOppositeDirection(direction: string): string {
    return OPPOSITE_DIRECTIONS[direction] || direction;
}

function scrollGradient3D(direction: string, isInset: boolean) {
    return css`
        background: linear-gradient(
            ${isInset ? 'to ' + getOppositeDirection(direction) : direction},
            rgba(0, 0, 0, 0.08) 0%,
            rgba(0, 0, 0, 0.02) 50%,
            rgba(0, 0, 0, 0) 100%
        );
    `;
}

function scrollGradientFlat(
    direction: string,
    isInset: boolean,
    flatColor: string
) {
    return css`
        background: linear-gradient(
            ${isInset ? 'to ' + getOppositeDirection(direction) : direction},
            ${flatColor} 0%,
            ${colors.transparent} 100%
        );
    `;
}

export function scrollShadow({
    isShadowActive,
    size: _size,
    direction,
    isInset = false,
    isFlat = false,
    flatColor = colors.white,
    offsetStart = 0,
    offsetEnd = 0,
    borderRadius = 0
}: {
    isShadowActive: boolean;
    size: number;
    direction: 'top' | 'bottom' | 'left' | 'right';
    isInset?: boolean;
    isFlat?: boolean;
    flatColor?: string;
    offsetStart?: number;
    offsetEnd?: number;
    borderRadius?: number | string;
}) {
    const size = isShadowActive ? _size - offsetStart - offsetEnd : 0;
    const dimension = isInset ? 0 : -size;

    const borderRadiusValue =
        typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;

    const COMMON_SCROLL_SHADOW_CSS = css`
        content: '';
        position: absolute;
        transition: 100ms;
        z-index: 1;
        pointer-events: none;
    `;

    const gradientFn = isFlat ? scrollGradientFlat : scrollGradient3D;

    switch (direction) {
        case 'top':
            return css`
                &:before {
                    ${COMMON_SCROLL_SHADOW_CSS}
                    left: 0px;
                    right: 0px;
                    top: ${dimension}px;
                    height: ${size}px;
                    ${borderRadiusValue
                        ? css`
                              border-top-left-radius: ${borderRadiusValue};
                              border-top-right-radius: ${borderRadiusValue};
                          `
                        : ''}
                    ${gradientFn('to top', isInset, flatColor)}
                }
            `;
        case 'bottom':
            return css`
                &:after {
                    ${COMMON_SCROLL_SHADOW_CSS}
                    left: 0px;
                    right: 0px;
                    bottom: ${dimension}px;
                    height: ${size}px;
                    ${borderRadiusValue
                        ? css`
                              border-bottom-left-radius: ${borderRadiusValue};
                              border-bottom-right-radius: ${borderRadiusValue};
                          `
                        : ''}
                    ${gradientFn('to bottom', isInset, flatColor)}
                }
            `;
        case 'left':
            return css`
                &:before {
                    ${COMMON_SCROLL_SHADOW_CSS}
                    top: ${0 + offsetStart}px;
                    bottom: ${offsetEnd}px;
                    left: ${dimension}px;
                    width: ${size}px;
                    ${borderRadiusValue
                        ? css`
                              border-top-left-radius: ${borderRadiusValue};
                              border-bottom-left-radius: ${borderRadiusValue};
                          `
                        : ''}
                    ${gradientFn('to left', isInset, flatColor)}
                }
            `;
        case 'right':
            return css`
                &:after {
                    ${COMMON_SCROLL_SHADOW_CSS}
                    top: ${0 + offsetStart}px;
                    bottom: ${offsetEnd}px;
                    right: ${dimension}px;
                    width: ${size}px;
                    ${borderRadiusValue
                        ? css`
                              border-top-right-radius: ${borderRadiusValue};
                              border-bottom-right-radius: ${borderRadiusValue};
                          `
                        : ''}
                    ${gradientFn('to right', isInset, flatColor)}
                }
            `;
    }
}

/* Font family mixins */
export const proximaNova = css`
    font-family: ${_proximaNova};
`;

/* Text mixins */
export const truncatedText = css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

/**
 * This mixin is used to truncate text with ellipsis and clamp it to a certain number of lines
 *It cannot have the "white-space: nowrap;" property because it will not work with the -webkit-line-clamp property.
 */
export function lineClampedText(lineClamp: number) {
    if (lineClamp && Number.isInteger(lineClamp)) {
        return css`
            -webkit-line-clamp: ${lineClamp};
            -webkit-box-orient: vertical;
            display: -webkit-box;
            overflow: hidden;
            white-space: initial;
            text-overflow: ellipsis;
        `;
    }
}

export const absoluteAll = css`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
`;

export const absoluteCentered = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

export const absoluteCenteredVertical = css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

// this mixin makes sure that print styles apply also in download pdf
export const printCss = (cssString: string) => css`
    .print-mode & {
        ${cssString}
    }
    @media print {
        ${cssString}
    }
`;

export const hideInPrint = printCss(`display: none;`);

export const showInPrintOnly = (
    displayProp: CSSProperties['display'] = 'block'
) => css`
    display: none;

    ${printCss(`display: ${displayProp};`)}
`;

export const setIconFillColor = (
    color: string,
    { noTransition }: { noTransition?: boolean } = {}
) => css`
    i {
        svg {
            path,
            polygon,
            circle,
            rect {
                fill: ${color};
                ${noTransition ? '' : transition('fill')};
            }
        }
    }
`;

// angular css removes outline for all elements.
// This mixin brings it back for focus visible
export const revertOutlineForFocusVisible = css`
    &:focus-visible {
        outline: revert;
    }
`;

export const inheritBorderRadiusColumn = css`
    &:first-of-type {
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
    }
    &:last-of-type {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
    }
`;

export const inheritBorderRadiusRow = css`
    &:first-of-type {
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
    }
    &:last-of-type {
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
    }
`;

// inset is used for cases where overflow is hidden
export const focusState = ({ inset = false }: { inset?: boolean } = {}) => css`
    &:focus-visible, 
    // data-focus-visible is used to apply focus state to the container element when the HBActionArea is used.
    &[data-focus-visible] {
        box-shadow: 0px 0px 0px 1px ${vars.colors.surface.container},
            0px 0px 0px 3px ${vars.colors.stroke.keyboardFocus}
                ${inset ? 'inset' : ''};
    }
`;

// This mixin is used to apply a focus outline to the container.
export const focusOutlineState = css`
    outline: 2px solid transparent;

    &:focus-visible,
    // data-focus-visible is used to apply focus state to the container element when the HBActionArea is used.
    &[data-focus-visible] {
        outline-color: ${vars.colors.stroke.keyboardFocus};
    }
`;

/**
 * Minimal reset for button/link elements to remove browser and global styles.
 */
export const resetInteractiveElement = css`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    box-sizing: border-box;
    appearance: none;
    outline: none;
`;

const interactiveSelectors = [
    'button',
    'a',
    'input',
    'select',
    'textarea',
    '[role="button"]',
    '[tabindex="0"]'
].join(', ');

const disabledSelectors = [
    ':disabled',
    '[aria-disabled="true"]',
    '[inert]'
].join(', ');

export const directHoverSelectorBase = `:hover:not(:has(:is(${interactiveSelectors}):not(:is(${disabledSelectors})):hover))`;
export const directHoverGlobalSelector = `*${directHoverSelectorBase}`;
export const directHoverSelector = `&${directHoverSelectorBase}`;

// AI mixins

// --AI-Floating-stroke
export const aiFloatingStroke = css`
  radial-gradient(
    100% 100% at 0% 50%,
    rgba(${hexToRgbString(colors.ai_purple)}, 0.30) 0%,
    rgba(${hexToRgbString(colors.ai_lavender)}, 0.30) 33%,
    rgba(${hexToRgbString(colors.ai_pink)}, 0.30) 60%,
    rgba(${hexToRgbString(colors.ai_yellow)}, 0.30) 150%
  )
`;

// --AI-Inline-stroke
export const aiInlineStroke = css`
  radial-gradient(
    682.63% 100% at 0% 50%,
    rgba(${hexToRgbString(colors.ai_purple)}, 0.25) 0%,
    rgba(${hexToRgbString(colors.ai_amaranth)}, 0.25) 50%,
    rgba(${hexToRgbString(colors.ai_lavender)}, 0.25) 100%
  )
`;

export const aiInlineFill = css`
  linear-gradient(
    265deg,
    rgba(${hexToRgbString(colors.ai_pink)}, 0.08) 0%,
    rgba(${hexToRgbString(colors.ai_purple)}, 0.05) 28.5%,
    rgba(${hexToRgbString(colors.ai_lavender)}, 0.04) 64.36%,
    rgba(${hexToRgbString(colors.white)}, 0.04) 91.94%
  )
`;

export const aiInlineFillBackground = css`
    background: ${aiInlineFill}, ${colors.white};
`;

export const aiAccentLineGradient = css`
  linear-gradient(
    180deg,
    rgba(${hexToRgbString(colors.ai_lavender)}, 0.50) 0%,
    rgba(${hexToRgbString(colors.ai_purple)}, 0.50) 50%,
    rgba(${hexToRgbString(colors.ai_amaranth)}, 0.50) 100%
  )
`;

export const aiDarkenedGradientBackground = css`
    background: linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.2) 0%,
            rgba(0, 0, 0, 0.2) 100%
        ),
        radial-gradient(
            72.39% 50% at 50% 50%,
            rgba(${hexToRgbString(colors.ai_pink)}, 0.2) 0%,
            rgba(${hexToRgbString(colors.ai_lavender)}, 0.2) 50%,
            rgba(${hexToRgbString(colors.ai_purple)}, 0.2) 100%
        );
`;

export const aiFullscreenFill = css`
  radial-gradient(
    171.83% 147.66% at 104.45% -4.38%, 
    rgba(${hexToRgbString(colors.ai_yellow)}, 0.08) 0%, 
    rgba(${hexToRgbString(colors.ai_pink)}, 0.16) 9.57%, 
    rgba(${hexToRgbString(colors.ai_purple)}, 0.05) 38.7%, 
    rgba(${hexToRgbString(colors.lavender500)}, 0.03) 78.26%, 
    rgba(${hexToRgbString(colors.white)}, 0.04) 100%
  )
`;

export const aiFullscreenFillBackground = css`
    background: ${aiFullscreenFill}, ${colors.white};
`;

export const aiButtonGradient = `linear-gradient(
            264deg,
            rgba(246, 209, 255, 0.08) 0%,
            rgba(94, 48, 227, 0.05) 31%,
            rgba(128, 151, 243, 0.04) 70%,
            rgba(255, 255, 255, 0.04) 100%
        ),
        ${colors.white}`;

export const aiButtonBackground = css`
    background: ${aiButtonGradient};
`;

export const aiFloatingBoxShadow = css`
    box-shadow: 0px 20px 40px 0px rgba(32, 12, 90, 0.2);
`;

export const aiLayeredBoxShadow = css`
    box-shadow: 0px 0px 10px 0px rgba(${hexToRgbString(colors.blue50)}, 0.08),
        0px 14px 28px 0px rgba(${hexToRgbString(colors.blue50)}, 0.15);
`;

// Base gradient border, if the same container has border-radius/box-shadow, please use adaptiveGradientBorder
export const baseGradientBorder = (borderGradient = aiFloatingStroke) => css`
    border: 1px solid transparent;
    border-image: ${borderGradient} 1;
    background-clip: padding-box;
`;

export const adaptiveGradientBorder = (
    background = aiFloatingStroke,
    borderRadius = '',
    borderWidth = '1px',
    inset = '0'
) => {
    // When inset is negative, don't add borderWidth since element extends outside
    // When inset is zero, add borderWidth to account for padding
    const hasNegativeInset = inset.startsWith('-');
    const pseudoBorderRadius = borderRadius
        ? borderRadius
              .split(' ')
              .map(value =>
                  hasNegativeInset ? value : `calc(${value} + ${borderWidth})`
              )
              .join(' ')
        : 'inherit';

    return css`
        position: relative;
        border-radius: ${borderRadius};

        &::before {
            content: '';
            position: absolute;
            inset: ${inset};
            border-radius: ${pseudoBorderRadius};
            padding: ${borderWidth};
            background: ${background};
            mask: linear-gradient(#fff 0 0) content-box,
                linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
        }
    `;
};

export const showWhenRowHoveredOrFocusedWithin = (
    forceVisible?: boolean
) => css`
    ${transition('opacity')}
    opacity: 0;
    tr:hover &,
    tr:focus-within & {
        opacity: 1;
    }
    ${forceVisible ? 'opacity: 1;' : ''}
`;

export const UnstyledButton = styled.button`
    ${resetInteractiveElement}
    cursor: pointer;

    &:hover,
    &:focus,
    &:focus-visible {
        background-color: transparent;
    }
`;
